/**
 * Dr. Mani's Magic Quiz — copy library.
 * Verbatim from dr-mm-files/dmm-quiz-spec.json -> library.
 * 10 archetypes, 6 profiles, 5 key insights, need diagnoses, segment morsels,
 * pain/villain blocks, and the three pillar snippets.
 */
import type { Archetype, Profile, KeyInsight, PillarSnippet, NeedKey } from './types';

export const DMM_ARCHETYPES: Archetype[] = [
  {
    id: 'provider',
    name: 'The Provider',
    triggered_by: 'Grows vegetables or herbs',
    core_reading:
      "Be honest. Part of you wants to know you could feed your family without the store.\nThat is not a small thing. It is the oldest kind of safety there is.\nYou garden to eat, not to decorate, and there is something honest in pulling dinner out of your own dirt.\nYou count a good year by what made it to the table, not into the bank.\nHere is what no one told you. The harvest was never about your thumb. It was about the soil.\nFeed the ground the right way, and it feeds your people back, season after season.",
    core_reading_lines: [
      'Be honest. Part of you wants to know you could feed your family without the store.',
      'That is not a small thing. It is the oldest kind of safety there is.',
      'You garden to eat, not to decorate, and there is something honest in pulling dinner out of your own dirt.',
      'You count a good year by what made it to the table, not into the bank.',
      'Here is what no one told you. The harvest was never about your thumb. It was about the soil.',
      'Feed the ground the right way, and it feeds your people back, season after season.',
    ],
    superpower: 'You can turn a patch of dirt into a season of dinners.',
    blind_spot:
      'You pour everything into the plants and forget the soil feeds them. Starve the ground, and the best seeds in the world give you nothing. Feed it first.',
  },
  {
    id: 'color_bringer',
    name: 'The Color Bringer',
    triggered_by: 'Grows flowers or ornamentals',
    core_reading:
      "Some people see a yard. You see color that has not happened yet.\nThat is rarer than you think.\nYou are the one who plants beauty on purpose, who notices the bloom everyone else walks right past.\nA flower is your way of saying you made this place more alive than you found it.\nBut here is the secret every great bloom keeps. The color is made underground, at the roots, long before you ever see it.\nGive the roots real life and real food, and your blooms run brighter and longer than you thought they could.",
    core_reading_lines: [
      'Some people see a yard. You see color that has not happened yet.',
      'That is rarer than you think.',
      'You are the one who plants beauty on purpose, who notices the bloom everyone else walks right past.',
      'A flower is your way of saying you made this place more alive than you found it.',
      'But here is the secret every great bloom keeps. The color is made underground, at the roots, long before you ever see it.',
      'Give the roots real life and real food, and your blooms run brighter and longer than you thought they could.',
    ],
    superpower: 'You make a place more beautiful just by tending it.',
    blind_spot:
      'You chase the flower and forget the flower is built at the root. Feed the top, and you get a week of color. Feed the root, and you get a season.',
  },
  {
    id: 'groundskeeper',
    name: 'The Groundskeeper',
    triggered_by: 'Has a lawn, lawn is the focus',
    core_reading:
      "You already know the feeling. A car slows down at your fence, just to look.\nThat is what you are after, and there is no shame in it.\nA lawn is the first thing the street sees, and you want yours right, even, green, and yours.\nYou earned that the hard way, mowing and watering and trying everything the bag promised.\nHere is what the bag never told you. A great lawn is grown from the soil up, not the bag down.\nWake the living soil under your grass, and it comes in thicker than any chemical ever made it.",
    core_reading_lines: [
      'You already know the feeling. A car slows down at your fence, just to look.',
      'That is what you are after, and there is no shame in it.',
      'A lawn is the first thing the street sees, and you want yours right, even, green, and yours.',
      'You earned that the hard way, mowing and watering and trying everything the bag promised.',
      'Here is what the bag never told you. A great lawn is grown from the soil up, not the bag down.',
      'Wake the living soil under your grass, and it comes in thicker than any chemical ever made it.',
    ],
    superpower: 'You can make a yard look cared for from the road.',
    blind_spot:
      'You reach for another bag when the fix is already under your feet. The salt greened it for a week and killed the soil for a year. Feed the dirt, not the blade.',
  },
  {
    id: 'plant_parent',
    name: 'The Plant Parent',
    triggered_by: 'Grows houseplants',
    core_reading:
      "Your windowsills are full, and so is the way you talk about them.\nYou are the one who brings the green inside, into the rooms where you actually live.\nA plant is not decoration to you. It is something you keep alive, and that says something good about you.\nYou have the heart for it. What you have been missing is the dirt.\nBecause in a pot, the soil is the whole world, and most bagged mix rots and drowns the roots.\nGet the home inside that pot right, and they stop dying on you and start filling the window.",
    core_reading_lines: [
      'Your windowsills are full, and so is the way you talk about them.',
      'You are the one who brings the green inside, into the rooms where you actually live.',
      'A plant is not decoration to you. It is something you keep alive, and that says something good about you.',
      'You have the heart for it. What you have been missing is the dirt.',
      'Because in a pot, the soil is the whole world, and most bagged mix rots and drowns the roots.',
      'Get the home inside that pot right, and they stop dying on you and start filling the window.',
    ],
    superpower: 'You can keep a room full of living things thriving.',
    blind_spot:
      'You water out of love, and love can drown a root in the wrong soil. Give the roots a home that breathes, and they forgive a heavy hand.',
  },
  {
    id: 'orchard_keeper',
    name: 'The Orchard Keeper',
    triggered_by: 'Grows fruit trees, berries, or grapes',
    core_reading:
      "You think in seasons, not weekends, and almost nobody does anymore.\nA fruit tree is a promise to your future self, and you are the kind who keeps it.\nYou are planting for the harvest that is coming, not just the one in front of you.\nThat patience is rare, and it pays in fruit nobody can buy.\nHere is the part that shortens the wait. A tree feeds through living roots, not through the branches you keep watching.\nGive it living soil, real food, and a home that does not rot, and you get fruit in years, not decades.",
    core_reading_lines: [
      'You think in seasons, not weekends, and almost nobody does anymore.',
      'A fruit tree is a promise to your future self, and you are the kind who keeps it.',
      'You are planting for the harvest that is coming, not just the one in front of you.',
      'That patience is rare, and it pays in fruit nobody can buy.',
      'Here is the part that shortens the wait. A tree feeds through living roots, not through the branches you keep watching.',
      'Give it living soil, real food, and a home that does not rot, and you get fruit in years, not decades.',
    ],
    superpower: 'You can grow fruit other people only buy.',
    blind_spot:
      'You watch the branches when the whole tree is fed at the root. Stare up all you want, the fix is down.',
  },
  {
    id: 'curator',
    name: 'The Curator',
    triggered_by: 'Grows succulents, cactus, or tropicals',
    core_reading:
      "You do not collect plants. You collect character.\nThe spiky, the strange, the survivors, the ones with a story.\nYou are drawn to things that ask for little and reward you anyway, and that taste says something about you.\nYour shelf is a little museum, and you are the one who keeps it.\nHere is where most collections die. The wrong dirt holds water and rots the very roots you were trying to protect.\nGive them a home that drains and breathes, and they thrive on their own terms, the way you picked them to.",
    core_reading_lines: [
      'You do not collect plants. You collect character.',
      'The spiky, the strange, the survivors, the ones with a story.',
      'You are drawn to things that ask for little and reward you anyway, and that taste says something about you.',
      'Your shelf is a little museum, and you are the one who keeps it.',
      'Here is where most collections die. The wrong dirt holds water and rots the very roots you were trying to protect.',
      'Give them a home that drains and breathes, and they thrive on their own terms, the way you picked them to.',
    ],
    superpower: 'You can keep alive what most people overwater to death.',
    blind_spot:
      'You love them, so you fuss, and these plants hate fuss. The right soil does the caring, so you do not have to.',
  },
  {
    id: 'purist',
    name: 'The Purist',
    triggered_by: 'Biggest reason is to do it the natural way',
    core_reading:
      "You read the back of the bag before most people read the front.\nSomewhere along the way you decided that how it is grown matters as much as what it grows.\nNo salt, no plastic prills, no sludge, nothing you would hide from your kids.\nThat is not picky. That is a standard, and your garden is where you hold the line.\nHere is the good news you were not told. The natural way is the simpler way, not the harder one.\nLiving soil, real food, a real home, and you never wonder what is in your dirt again.",
    core_reading_lines: [
      'You read the back of the bag before most people read the front.',
      'Somewhere along the way you decided that how it is grown matters as much as what it grows.',
      'No salt, no plastic prills, no sludge, nothing you would hide from your kids.',
      'That is not picky. That is a standard, and your garden is where you hold the line.',
      'Here is the good news you were not told. The natural way is the simpler way, not the harder one.',
      'Living soil, real food, a real home, and you never wonder what is in your dirt again.',
    ],
    superpower: 'You grow clean, and you never wonder what is in it.',
    blind_spot:
      'You can let perfect stall you, researching the right way while the season slips. Living soil is the right way. Start, and the rest sorts itself.',
  },
  {
    id: 'small_space_master',
    name: 'The Small-Space Master',
    triggered_by: 'Apartment, condo, patio, or small-space containers',
    core_reading:
      "You never let the lack of a yard stop you, did you.\nA balcony, a patio, one sunny sill, and you turn it into a garden.\nYou grow where you are instead of waiting for a place you do not have, and that is its own kind of grit.\nSome of the best growers alive have never owned an acre. You are proof.\nHere is the whole game in a pot. The soil inside it is the entire world your plant gets.\nFill it with life and a home that does not rot, and a single container out-grows a neglected yard.",
    core_reading_lines: [
      'You never let the lack of a yard stop you, did you.',
      'A balcony, a patio, one sunny sill, and you turn it into a garden.',
      'You grow where you are instead of waiting for a place you do not have, and that is its own kind of grit.',
      'Some of the best growers alive have never owned an acre. You are proof.',
      'Here is the whole game in a pot. The soil inside it is the entire world your plant gets.',
      'Fill it with life and a home that does not rot, and a single container out-grows a neglected yard.',
    ],
    superpower: 'You can grow more in a pot than most people grow in a yard.',
    blind_spot:
      'A pot is a small world that runs out fast, and tired soil is the ceiling on what you can grow. Keep it alive and fed, and your small space stops having a limit.',
  },
  {
    id: 'comeback_grower',
    name: 'The Comeback Grower',
    triggered_by: 'Plants keep dying, reviving struggling plants',
    core_reading:
      "You have buried a few. And you planted again anyway.\nMost people quit after the second dead plant. You read it, you adjust, you try again.\nThat is not a brown thumb. That is the most stubborn kind of green one.\nYou do not see a dying plant. You see one that has not been given what it needs yet.\nAnd you were right to keep going. The plant did not die because of you. It died hungry, or drowning, or in dead dirt.\nGive it the right three things, and watch what your hands can really do.",
    core_reading_lines: [
      'You have buried a few. And you planted again anyway.',
      'Most people quit after the second dead plant. You read it, you adjust, you try again.',
      'That is not a brown thumb. That is the most stubborn kind of green one.',
      'You do not see a dying plant. You see one that has not been given what it needs yet.',
      'And you were right to keep going. The plant did not die because of you. It died hungry, or drowning, or in dead dirt.',
      'Give it the right three things, and watch what your hands can really do.',
    ],
    superpower: 'You bring back what other people give up on.',
    blind_spot:
      'You blame yourself faster than anyone would blame you. It was the tools, not the thumb. Set that down, and you will grow circles around the people who never doubted themselves.',
  },
  {
    id: 'brave_beginner',
    name: 'The Brave Beginner',
    triggered_by: 'Brand new, just getting started',
    core_reading:
      "Here is the secret the experts will not admit. You are starting from the best place of all.\nYou have not learned the bad habits yet, the salt, the wrong dirt, the fear of killing things.\nYou were brave enough to begin, and beginning is the only thing that ever separated a gardener from someone who just wished.\nThe plants that died for other people taught them what to fear. You get to skip that.\nGet the soil right from day one, and you walk past years of trial and error the rest of us paid for.\nYour first real harvest is going to hook you for life.",
    core_reading_lines: [
      'Here is the secret the experts will not admit. You are starting from the best place of all.',
      'You have not learned the bad habits yet, the salt, the wrong dirt, the fear of killing things.',
      'You were brave enough to begin, and beginning is the only thing that ever separated a gardener from someone who just wished.',
      'The plants that died for other people taught them what to fear. You get to skip that.',
      'Get the soil right from day one, and you walk past years of trial and error the rest of us paid for.',
      'Your first real harvest is going to hook you for life.',
    ],
    superpower: 'You get to start right, with none of the bad habits.',
    blind_spot:
      'You think you need a green thumb. You do not. You need living soil and one season of proof, and you will never call yourself a brown thumb again.',
  },
];

// Why this matters: profiles diagnose the garden, never the person — heroic names + absolution copy.
export const DMM_PROFILES: Profile[] = [
  {
    id: 'root_rescuer',
    name: 'The Root Rescuer',
    triggered_by: 'Rot, soggy soil, fungus gnats, ugly mix',
    headline: 'Your roots have been drowning. You are the one about to give them air.',
    description:
      'Your plants were never weak, and you were never the problem. They were stuck in soil that holds water and rots roots from the bottom up. Give them a home that breathes, and they turn around in weeks.',
    regimen_text: 'Super Soil (the home) + Plant Super Boost (the life). Rice hulls on top.',
    need: 'HOME',
    pain_tag: 'Rot/soggy',
  },
  {
    id: 'nourisher',
    name: 'The Nourisher',
    triggered_by: 'Yellow, pale, slow, weak growth',
    headline: 'Your plants are not sick. They are hungry, and you are about to feed them right.',
    description:
      'Pale, slow, shy of blooms is not bad luck and it is not your thumb. It is hunger. Real food, fed slow, brings the green and the color roaring back.',
    regimen_text: 'Crab, Kelp & Amino Acids (the food) + Plant Super Boost (the life).',
    need: 'FOOD',
    pain_tag: 'Yellowing',
  },
  {
    id: 'soil_awakener',
    name: 'The Soil Awakener',
    triggered_by: 'Dead soil, used salt fertilizer, things just die',
    headline: 'Your dirt went quiet. You are the one who wakes it back up.',
    description:
      'Salt fertilizer and bagged mix quietly killed the living things in your soil. Dead dirt cannot grow a living plant, no matter how hard you try, and no thumb on earth could have done it. Put the life back, and the roots start eating again.',
    regimen_text: 'Plant Super Boost (the life) first.',
    need: 'LIFE',
    pain_tag: 'Dead soil',
  },
  {
    id: 'bloom_chaser',
    name: 'The Bloom Chaser',
    triggered_by: "Won't flower, won't fruit, small crop",
    headline: 'All those leaves and no payoff. The blooms are one fix away.',
    description:
      'A plant full of leaves but no flowers or fruit is not broken. It is hungry, and its soil is half asleep. Wake both, and the payoff you have been waiting for finally shows up.',
    regimen_text: 'Crab, Kelp & Amino Acids (the food) + Plant Super Boost (the life).',
    need: 'FOOD + LIFE',
    pain_tag: 'No bloom/fruit',
  },
  {
    id: 'whole_garden_builder',
    name: 'The Whole-Garden Builder',
    triggered_by: 'Just starting, or more than one thing going wrong',
    headline: 'You get to build it right, while everyone else is still undoing their mistakes.',
    description:
      'A clean slate is the best place to be. Give your garden all three things from day one, and skip the years of trial and error the rest of us paid for in dead plants.',
    regimen_text: 'The Bundle, all three: life, food, and home.',
    need: 'ALL',
    pain_tag: 'Starting/multiple',
  },
  {
    id: 'reviver',
    name: 'The Reviver',
    triggered_by: 'Bringing struggling plants back from the edge',
    headline: 'The plant you almost gave up on can still be the one you are proudest of.',
    description:
      'You do not quit on a plant, and you were right not to. Give it the full set, life, food, and a home, and watch it come back from the edge.',
    regimen_text: 'The Bundle, all three, as a rescue.',
    need: 'ALL',
    pain_tag: 'Reviving',
  },
];

export const DMM_KEY_INSIGHTS: KeyInsight[] = [
  {
    why_value: 'To grow my own food',
    why_tag: 'Grow-food',
    key_insight:
      "There is something in you that needs to know you could feed yourself if it came to it.\nThat is not a hobby. It is the oldest kind of security there is, and most people traded it away without noticing.\nEvery thing you pull from your own dirt is a small way of taking it back.",
    key_insight_lines: [
      'There is something in you that needs to know you could feed yourself if it came to it.',
      'That is not a hobby. It is the oldest kind of security there is, and most people traded it away without noticing.',
      'Every thing you pull from your own dirt is a small way of taking it back.',
    ],
  },
  {
    why_value: 'To make it beautiful',
    why_tag: 'Beauty',
    key_insight:
      "You are wired to make a place more alive than you found it.\nWhere others see a yard, you see what it could be.\nThat eye is rarer than you think, and your garden is where you finally get to use it.",
    key_insight_lines: [
      'You are wired to make a place more alive than you found it.',
      'Where others see a yard, you see what it could be.',
      'That eye is rarer than you think, and your garden is where you finally get to use it.',
    ],
  },
  {
    why_value: 'To be the one with the green thumb',
    why_tag: 'Mastery',
    key_insight:
      "You are tired of being the one whose plants die, and you are done with it.\nThere is a version of you that grows things easily, that friends come to for advice, and you can feel it is close.\nThis is the climb from brown thumb to green, and you are already on it.",
    key_insight_lines: [
      'You are tired of being the one whose plants die, and you are done with it.',
      'There is a version of you that grows things easily, that friends come to for advice, and you can feel it is close.',
      'This is the climb from brown thumb to green, and you are already on it.',
    ],
  },
  {
    why_value: 'For the peace it gives me',
    why_tag: 'Peace',
    key_insight:
      "Your garden gives you back something nobody can put a price on, an hour that belongs only to you.\nHands in the soil, phone inside, the day falling off your shoulders.\nIt is not just what you grow out there. It is what it grows in you.",
    key_insight_lines: [
      'Your garden gives you back something nobody can put a price on, an hour that belongs only to you.',
      'Hands in the soil, phone inside, the day falling off your shoulders.',
      'It is not just what you grow out there. It is what it grows in you.',
    ],
  },
  {
    why_value: 'To do it the natural way',
    why_tag: 'Natural',
    key_insight:
      "You believe the how matters as much as the what, and you will not trade clean for fast.\nThat is not stubbornness. It is integrity, and it is rarer every year.\nYour soil knows the difference, even when no one else is watching.",
    key_insight_lines: [
      'You believe the how matters as much as the what, and you will not trade clean for fast.',
      'That is not stubbornness. It is integrity, and it is rarer every year.',
      'Your soil knows the difference, even when no one else is watching.',
    ],
  },
];

export const DMM_NEED_DIAGNOSIS: Record<NeedKey, string> = {
  LIFE:
    "Your garden was missing life. Somewhere your dirt went dead, the salt fertilizer and the bagged mix killed the living things in it. Dead dirt cannot grow a living thing, no matter how hard you try. The fix is to put the life back, the microbes that feed the roots from underneath.",
  FOOD:
    "Your garden was missing food. Your plants were hungry, pale and slow and shy of blooms, running on empty. Not the salt kind that burns, the real kind that feeds slow. The fix is real food, fed on nature's schedule, that the roots can actually drink.",
  HOME:
    "Your garden was missing a home. Your roots were drowning, packed in a mix that rots down and squeezes the air out. A plant cannot breathe in a brick. The fix is a mineral home that stays open and alive for years.",
  'FOOD + LIFE':
    "Your garden was missing two things at once, real food and a living root zone. All those leaves and no payoff is a plant that is hungry and sitting in half-asleep soil. The fix is to feed it real food and wake the soil under it.",
  ALL:
    "Your garden was missing more than one thing. Some life, some food, and a better home, all at once. That is not bad luck, that is three small gaps adding up. The fix is the whole system together, which is also the smart buy.",
};

export const DMM_SEGMENT_MORSELS: Record<string, string> = {
  vegetables:
    'You grow food. That puts you in the oldest line of work there is. A vegetable grower is patient, hopeful, and a little stubborn, because every season is a fresh bet. Your matches feed that bet, so the harvest finally pays off.',
  flowers:
    'You grow beauty. You plant something just to make a place feel alive. Flower people see the world in color and want more of it around them. Your matches were built to run that color longer and brighter.',
  lawn:
    'You grow the carpet the whole street stands on. A lawn person likes things even, green, and theirs. It is pride you can see from the road. Your plan keeps it thick and alive without the bag of salt.',
  houseplants:
    'You grow life indoors. You bring the green into the rooms where you actually live. Plant parents are nurturers, and the indoor jungle is how it shows. Your matches keep that jungle thriving instead of slowly fading.',
  fruit_trees:
    'You grow patience that pays in fruit. A tree person thinks in seasons, not weekends. You are planting for the harvest that is coming. Your matches get you there faster, and heavier.',
  everything:
    'You grow the whole picture. You are a yard person, a bit of food, a bit of color, a bit of green inside. That is exactly who the three things were made for, because they feed all of it at once.',
};

export const DMM_PAIN_VILLAIN: Record<string, string> = {
  yellow_pale:
    'You said your leaves go yellow and weak. That is hunger, plain and simple. The salt food burned what little life was left, and the plant has been running on empty.',
  plants_die:
    'You said plants just die on you. They did not die because of you. They died in dead dirt, with nothing alive in it to feed a single root.',
  smell:
    'You said the fertilizer smells awful. That rotten-fish stink is cheap filler breaking down. Living soil does not smell like death. It smells like the ground after rain.',
  chemicals:
    'You said you are scared of the chemicals. You were right to be. Most bagged food is salt, and some of it begins as treated sewage sludge, which is where the forever chemicals ride in.',
  ugly_mix:
    'You said your mix turns ugly, green on top and hard as a brick. That is salt and dead peat doing what they always do. Real mineral soil stays dark, open, and clean for years.',
  rot_soggy:
    'You said your roots rot and the soil stays wet. The plant is drowning in a mix that holds water and squeezes out the air. A home that breathes fixes it fast.',
  no_bloom_fruit:
    'You said it will not bloom or fruit. All leaves and no payoff is a hungry plant in half-asleep soil. Feed it real food and wake the soil, and the payoff shows up.',
  gnats:
    'You said little flies hover around your pots. Fungus gnats live in soggy, rotting mix. Dry, living, mineral soil gives them nowhere to breed, and a drop of castile soap finishes them.',
  bugs:
    'You said bugs are chewing your plants. Healthy plants in living soil fight back on their own, and for the rest there is the gentle, natural pest trio.',
  no_directions:
    'You said nobody ever told you what to actually do. Every site says something different. Your field guide and this plan are the one clear answer, in plain steps.',
  lawn_rings:
    'You said your lawn has brown rings or thin, bare spots. That is dead soil under the grass, not a grass problem. Wake the soil and the lawn fills back in on its own.',
};

export const DMM_PILLAR_SNIPPETS: Record<string, PillarSnippet> = {
  plant_super_boost: {
    role: 'LIFE',
    short:
      'This is the life. A living liquid you pour over the soil. 1,227 kinds of fungi and bacteria, billions of them, in every drop. They wake the dirt up and feed the roots what they could never reach alone.',
    long:
      "This is Plant Super Boost, the life. It is a living liquid, not a chemical. In one drop there are 1,227 living kinds of fungi and bacteria, billions of cells. They wrap the roots, crowd out rot, and unlock food that was locked in the soil. They even make the plant's own flower and root hormones. You pour about two ounces in a gallon of water, over the soil, once a month. You cannot burn a plant with it, and you cannot overdose it, because it is life, not salt.",
  },
  crab_kelp_amino_acids: {
    role: 'FOOD',
    short:
      'This is the food. Our organic 7-4-4, real food from once-living things. It feeds slow, over about a month, and it will not burn. It smells faintly of the ocean, never of rot.',
    long:
      "This is our Crab, Kelp & Amino Acids, the food, and it is 7-4-4. Nitrogen for green leaves, phosphorus for roots and blooms, potassium for sturdy stems and fruit, plus calcium for strong cell walls and trace minerals. It does not force-feed the plant the way salt does. It feeds the microbes, and the microbes hand it to the roots on nature's schedule. One feeding lasts about 30 days. It will not burn, and you cannot overdose it. And it smells clean, like the ocean, never like rotting fish.",
  },
  super_soil: {
    role: 'HOME',
    short:
      'This is the home. A mineral soil that does not rot, pack, or grow algae. You plant into it once, and it stays open and alive for years.',
    long:
      'This is Super Soil, the home. It is mineral-based, not bark or peat or sawdust. South Texas sandy loam, parboiled rice hulls, washed coco coir, and biochar. Because it is mineral, it does not decompose. Wood-based mixes rot down, collapse, and squeeze the air out until the roots drown. This one stays heavy, stays open, and feeds oxygen to the roots for years. The biochar is a permanent home where the microbes multiply. You plant into it once, and refresh it once a year from the bottom. It is close to the last bag of soil you will ever buy.',
  },
  the_bundle: {
    role: 'ALL',
    short:
      'This is all three together, the Bundle. The life, the food, and the home, the whole system at once. It is the smart buy, because the three were built to work as one. Start it once, and your garden runs on it.',
  },
  lawn_variant: {
    role: 'LAWN',
    short:
      'For a lawn, the hero is Plant Super Boost. Mow, water, and pour the living microbes over the grass once a month. The food is optional, and the Super Soil is not for grass. We also point you to the Chameleon sprayer, the one hose-end that does not strain the living microbes out (we recommend it, we do not make it).',
  },
};
